/**
 * Held Zuora welcomes — reviewed manual release (migration 2026-10-03-006).
 *
 * A welcome is HELD when the re-fetch could not confirm a new signup (Zuora unavailable, the subscription list could
 * not be proven complete, account not found, no subscriptions). An active admin/superadmin checks the account in Zuora
 * and releases (stating what Zuora shows) or dismisses it. All three RPCs enforce the role server-side; every
 * suppression (import / Pardot / reconciliation exclusions, no longer active, email suppression, account holds, the
 * stop switch) is enforced by zuora_held_welcome_release itself, never by this page.
 */
import { supabase } from "./supabase";

export interface HeldWelcomeRow {
  deferred_id: string;
  contact_id: string;
  contact_name: string | null;
  contact_email: string | null;
  zuora_account_id: string;
  account_number: string | null;
  joined_at: string;
  held_at: string | null;
  hold_reason: string | null;
  attempts: number;
  listed_ids: string[] | null;
  last_fetch: Record<string, unknown> | null;
  evidence: Record<string, unknown> | null;
  join_classification: string | null;
  is_current_episode: boolean;
  contact_active: boolean;
  email_block: string | null;
  /** The server's own preflight for a release (null = ready). The release RPC runs the same check. */
  preflight: string | null;
  last_attempt: {
    attempted_at: string;
    completed: boolean;
    classification: string;
    enrolled: number | null;
    failed: { sequence_id: string; name: string; error: string }[] | null;
    by: string | null;
  } | null;
  review_action: string | null;
  review_classification: string | null;
  review_reason: string | null;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
}

export interface JourneyResult {
  trigger?: string;
  active_journeys?: number;
  enrolled?: number;
  already_enrolled?: number;
  /** R6-1: journeys whose own setting forbids a second run -- NOT scheduled */
  not_reenrollable?: number;
  enrolled_before?: number;
  skipped_conditions?: number;
  failed?: { sequence_id: string; name: string; error: string }[];
  complete?: boolean;
}

export interface HeldWelcomeResult {
  ok: boolean;
  /** released | release_incomplete (journeys failed: still held, retry after fixing) | refused | dismissed | ... */
  outcome: string;
  reason?: string;
  attempts?: number;
  release?: JourneyResult;
}

export async function getHeldWelcomes(
  includeReviewed: boolean,
): Promise<HeldWelcomeRow[]> {
  const { data, error } = await supabase.rpc("zuora_held_welcome_report", {
    p_include_reviewed: includeReviewed,
  });
  if (error) {
    throw new Error(error.message);
  }
  return (data ?? []) as HeldWelcomeRow[];
}

export async function releaseHeldWelcome(
  deferredId: string,
  classification: "new" | "reinstatement",
  reason: string,
): Promise<HeldWelcomeResult> {
  const { data, error } = await supabase.rpc("zuora_held_welcome_release", {
    p_deferred_id: deferredId,
    p_classification: classification,
    p_reason: reason,
  });
  if (error) {
    throw new Error(error.message);
  }
  return data as HeldWelcomeResult;
}

export async function dismissHeldWelcome(
  deferredId: string,
  reason: string,
): Promise<HeldWelcomeResult> {
  const { data, error } = await supabase.rpc("zuora_held_welcome_dismiss", {
    p_deferred_id: deferredId,
    p_reason: reason,
  });
  if (error) {
    throw new Error(error.message);
  }
  return data as HeldWelcomeResult;
}
